import * as Sentry from '@sentry/react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button, ICON_SIZE } from '../design-system';
import ErrorDetails from './ErrorDetails';

function ErrorFallback({ error }) {
  const { t } = useTranslation('errors');

  // If we're in the middle of a cleanup→reload/redirect cycle, show a calm
  // "updating" screen instead of the scary error page. The cleanup mutates
  // Legend-State observables which can crash subscribed React components
  // before window.location.reload() fires.
  if (typeof window !== 'undefined' && window.__reedle_navigating) {
    return (
      <div style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--rds-space-6)',
        backgroundColor: 'var(--rds-surface-canvas)',
        fontFamily: 'var(--rds-font-ui)',
        gap: 'var(--rds-space-4)',
      }}>
        <div style={{ opacity: 0.5 }}>
          <Loader2 size={ICON_SIZE.xxl} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
        <p style={{
          margin: 0,
          fontSize: 'var(--rds-type-body-size)',
          color: 'var(--rds-ink-secondary)',
        }}>
          {t('errorBoundary.updating', 'Updating...')}
        </p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div className="rds-empty-page">
      <section className="rds-empty rds-empty--framed">
        <div className="rds-empty-header">
          <span className="rds-empty-media rds-empty-media--error" aria-hidden="true">
            <AlertCircle size={ICON_SIZE.xl} />
          </span>
          <h1 className="rds-empty-title">{t('errorBoundary.title')}</h1>
          <p className="rds-empty-description">{t('errorBoundary.message')}</p>
        </div>

        <div className="rds-empty-content">
          {/* Error details — visible so users can act and report bugs */}
          {error && (
            <ErrorDetails
              error={error}
              onRetry={() => window.location.reload()}
            />
          )}

          <div className="rds-empty-actions">
            <Button onClick={() => { window.location.href = '/'; }}>
              {t('errorBoundary.goHome')}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default function ErrorBoundary({ children }) {
  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback} showDialog={false}>
      {children}
    </Sentry.ErrorBoundary>
  );
}
